import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { Debt, DebtPayment } from "@/lib/types";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Landmark, Receipt } from "lucide-react";
import { DateEditInline } from "@/components/DateEditInline";

const today = () => new Date().toLocaleDateString("en-CA");

const DebtsPage: React.FC = () => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Debt | null>(null);
  const [payments, setPayments] = useState<DebtPayment[]>([]);
  const [form, setForm] = useState({ creditor: "", description: "", principal: "", interest_rate: "", debt_type: "prestamo_por_pagar", cash_inflow: true, inflow_date: today() });
  const [disb, setDisb] = useState({ amount: "", date: today() });
  const [pay, setPay] = useState({ capital: "", interes: "", comision: "", recargo: "", note: "", is_mixed: false, date: today() });
  const [debtTypes, setDebtTypes] = useState<{ key: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/debts");
    setDebts(res.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    client.get("/cash/categories").then((r) => setDebtTypes(r.data.debt_types));
  }, []);

  const openDebt = async (d: Debt) => {
    setSelected(d);
    setDisb({ amount: String(d.principal), date: today() });
    const res = await client.get(`/debts/${d.id}/payments`);
    setPayments(res.data);
  };

  const createDebt = async () => {
    if (!form.creditor || !form.principal) {
      toast.error("Acreedor y capital son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await client.post("/debts", {
        creditor: form.creditor,
        description: form.description,
        principal: parseFloat(form.principal),
        interest_rate: parseFloat(form.interest_rate) || 0,
        debt_type: form.debt_type,
        cash_inflow: form.cash_inflow,
        inflow_date: form.cash_inflow ? form.inflow_date : undefined,
      });
      toast.success("Deuda registrada");
      setCreateOpen(false);
      setForm({ creditor: "", description: "", principal: "", interest_rate: "", debt_type: "prestamo_por_pagar", cash_inflow: true, inflow_date: today() });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const registerDisbursement = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await client.post(`/debts/${selected.id}/disbursement`, { amount: parseFloat(disb.amount) || 0, date: disb.date });
      setSelected(r.data);
      toast.success("Ingreso del préstamo registrado en Caja");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const registerPayment = async () => {
    if (!selected) return;
    const body = {
      capital: parseFloat(pay.capital) || 0,
      interes: parseFloat(pay.interes) || 0,
      comision: parseFloat(pay.comision) || 0,
      recargo: parseFloat(pay.recargo) || 0,
      is_mixed: pay.is_mixed,
      note: pay.note,
      date: pay.date ? `${pay.date}T12:00:00Z` : undefined,
    };
    if (body.capital + body.interes + body.comision + body.recargo <= 0) {
      toast.error("El pago debe ser mayor a cero");
      return;
    }
    setSaving(true);
    try {
      await client.post(`/debts/${selected.id}/payments`, body);
      toast.success("Pago registrado");
      setPay({ capital: "", interes: "", comision: "", recargo: "", note: "", is_mixed: false, date: pay.date });
      const [d, p] = await Promise.all([client.get(`/debts/${selected.id}`), client.get(`/debts/${selected.id}/payments`)]);
      setSelected(d.data);
      setPayments(p.data);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const totalOutstanding = debts.reduce((s, d) => s + (d.status !== "paid" ? d.outstanding : 0), 0);
  const payTotal =
    (parseFloat(pay.capital) || 0) + (parseFloat(pay.interes) || 0) + (parseFloat(pay.comision) || 0) + (parseFloat(pay.recargo) || 0);

  return (
    <div data-testid="debts-page">
      <PageHeader
        title="Deudas"
        subtitle={`Deuda pendiente total: ${money(totalOutstanding)}`}
        actions={
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="create-debt-button"
            className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000]"
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            Registrar deuda
          </button>
        }
      />

      <div className="border border-slate-200 bg-white">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
          </div>
        ) : debts.length === 0 ? (
          <EmptyState message="No hay deudas registradas" testid="debts-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="debts-table">
            <thead>
              <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Acreedor</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3 text-right">Capital</th>
                <th className="px-5 py-3 text-right">Pagado</th>
                <th className="px-5 py-3 text-right">Saldo</th>
                <th className="px-5 py-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {debts.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => openDebt(d)}
                  className="cursor-pointer border-b border-slate-50 hover:bg-slate-50/50"
                  data-testid={`debt-row-${d.id}`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 font-medium text-slate-800">
                      <Landmark className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                      {d.creditor}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{d.description || "—"}</td>
                  <td className="px-5 py-3 text-right tabular text-slate-700">{money(d.principal)}</td>
                  <td className="px-5 py-3 text-right tabular text-emerald-600">{money(d.total_paid)}</td>
                  <td className="px-5 py-3 text-right font-medium tabular text-slate-900">{money(d.outstanding)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={d.status} label={d.status === "paid" ? "Liquidada" : "Activa"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create debt dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none" data-testid="create-debt-dialog">
          <DialogHeader>
            <DialogTitle>Registrar nueva deuda</DialogTitle>
            <DialogDescription>Captura el acreedor, tipo y capital de la deuda.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Acreedor</Label>
              <Input
                value={form.creditor}
                onChange={(e) => setForm({ ...form, creditor: e.target.value })}
                data-testid="debt-creditor-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Descripción</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="debt-description-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tipo de deuda</Label>
              <Select value={form.debt_type} onValueChange={(v) => setForm({ ...form, debt_type: v })}>
                <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="debt-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {debtTypes.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Capital (MXN)</Label>
                <Input
                  type="number"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  data-testid="debt-principal-input"
                  className="mt-1.5 rounded-none border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Tasa interés (%)</Label>
                <Input
                  type="number"
                  value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                  data-testid="debt-rate-input"
                  className="mt-1.5 rounded-none border-slate-300"
                />
              </div>
            </div>
            <div className="border-l-2 border-emerald-300 bg-emerald-50 p-3">
              <label className="flex items-center gap-2 text-xs text-emerald-800">
                <input
                  type="checkbox"
                  checked={form.cash_inflow}
                  onChange={(e) => setForm({ ...form, cash_inflow: e.target.checked })}
                  data-testid="debt-cash-inflow"
                  className="h-4 w-4 accent-[#DD0000]"
                />
                El dinero ingresó a caja / banco (registra la entrada "Préstamo recibido")
              </label>
              {form.cash_inflow && (
                <div className="mt-2">
                  <Label className="text-xs text-emerald-800">Fecha del ingreso</Label>
                  <Input type="date" value={form.inflow_date} onChange={(e) => setForm({ ...form, inflow_date: e.target.value })} data-testid="debt-inflow-date" className="mt-1 rounded-none border-emerald-300 bg-white" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={createDebt}
              disabled={saving}
              data-testid="debt-submit"
              className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Debt detail panel */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto rounded-none border-l border-slate-200 p-0 sm:max-w-[460px]" data-testid="debt-detail-panel">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-lg font-bold tracking-tight">{selected?.creditor}</SheetTitle>
            <SheetDescription>Detalle de la deuda, pagos y registro de nuevos abonos.</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="space-y-6 p-6">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Capital</div>
                  <div className="mt-1 text-sm font-medium tabular text-slate-900">{money(selected.principal)}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pagado</div>
                  <div className="mt-1 text-sm font-medium tabular text-emerald-600">{money(selected.total_paid)}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Saldo</div>
                  <div className="mt-1 text-sm font-medium tabular text-slate-900">{money(selected.outstanding)}</div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 text-xs">
                <div><span className="text-slate-400">Capital:</span> <span className="tabular">{money(selected.paid_capital)}</span></div>
                <div><span className="text-slate-400">Interés:</span> <span className="tabular">{money(selected.paid_interest)}</span></div>
                <div><span className="text-slate-400">Comisión:</span> <span className="tabular">{money(selected.paid_commission)}</span></div>
                <div><span className="text-slate-400">Recargo:</span> <span className="tabular">{money(selected.paid_surcharge)}</span></div>
              </div>

              {selected.disbursement_movement_id ? (
                <p className="text-xs text-emerald-700" data-testid="debt-disbursement-registered">
                  Ingreso del préstamo registrado en Caja ({fmtDate(selected.disbursement_date || "")}).
                </p>
              ) : (
                <div className="border-l-2 border-emerald-300 bg-emerald-50 p-4" data-testid="debt-disbursement-form">
                  <h4 className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-800">Registrar ingreso del préstamo</h4>
                  <p className="mb-3 text-[11px] text-emerald-700">Si el dinero prestado entró a tu cuenta, regístralo como entrada en Caja.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-emerald-800">Monto recibido</Label>
                      <Input type="number" value={disb.amount} onChange={(e) => setDisb({ ...disb, amount: e.target.value })} data-testid="disb-amount" className="mt-1 rounded-none border-emerald-300 bg-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-emerald-800">Fecha</Label>
                      <Input type="date" value={disb.date} onChange={(e) => setDisb({ ...disb, date: e.target.value })} data-testid="disb-date" className="mt-1 rounded-none border-emerald-300 bg-white" />
                    </div>
                  </div>
                  <button
                    onClick={registerDisbursement}
                    disabled={saving}
                    data-testid="disb-submit"
                    className="mt-3 flex items-center gap-2 rounded-none bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-800 disabled:opacity-60"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Registrar ingreso a caja
                  </button>
                </div>
              )}

              {/* Payment form */}
              <div className="border border-slate-200 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  <Receipt className="h-3.5 w-3.5" /> Registrar pago
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Capital</Label>
                    <Input type="number" value={pay.capital} onChange={(e) => setPay({ ...pay, capital: e.target.value })} data-testid="pay-capital" className="mt-1 rounded-none border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Interés</Label>
                    <Input type="number" value={pay.interes} onChange={(e) => setPay({ ...pay, interes: e.target.value })} data-testid="pay-interes" className="mt-1 rounded-none border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Comisión</Label>
                    <Input type="number" value={pay.comision} onChange={(e) => setPay({ ...pay, comision: e.target.value })} data-testid="pay-comision" className="mt-1 rounded-none border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Recargo</Label>
                    <Input type="number" value={pay.recargo} onChange={(e) => setPay({ ...pay, recargo: e.target.value })} data-testid="pay-recargo" className="mt-1 rounded-none border-slate-300" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-500">Fecha del pago</Label>
                  <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="pay-date" className="mt-1 rounded-none border-slate-300" />
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-slate-600" data-testid="pay-mixed-label">
                  <input
                    type="checkbox"
                    checked={pay.is_mixed}
                    onChange={(e) => setPay({ ...pay, is_mixed: e.target.checked })}
                    data-testid="pay-mixed"
                    className="h-4 w-4 accent-[#DD0000]"
                  />
                  Pago mixto no desglosado
                </label>
                <div className="mt-3">
                  <Label className="text-xs text-slate-500">Nota</Label>
                  <Textarea value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="pay-note" rows={2} className="mt-1 rounded-none border-slate-300" />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    Total: <span className="font-medium tabular text-slate-900">{money(payTotal)}</span>
                  </span>
                  <button
                    onClick={registerPayment}
                    disabled={saving}
                    data-testid="pay-submit"
                    className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Registrar pago
                  </button>
                </div>
              </div>

              {/* Payments history */}
              <div>
                <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Pagos registrados</h4>
                {payments.length === 0 ? (
                  <p className="text-xs text-slate-400">Sin pagos aún.</p>
                ) : (
                  <div className="space-y-2" data-testid="payments-list">
                    {payments.map((p) => (
                      <div key={p.id} className="border border-slate-100 bg-slate-50/50 p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium tabular text-slate-800">{money(p.total)}</span>
                          <DateEditInline
                            date={p.date}
                            testid={`payment-date-${p.id}`}
                            onSave={async (d) => {
                              await client.patch(`/debts/${selected.id}/payments/${p.id}`, { date: d });
                              const r = await client.get(`/debts/${selected.id}/payments`);
                              setPayments(r.data);
                            }}
                          />
                        </div>
                        <div className="mt-1 text-slate-500">
                          Cap {money(p.capital)} · Int {money(p.interes)} · Com {money(p.comision)} · Rec {money(p.recargo)}
                        </div>
                        {p.note && <div className="mt-1 text-slate-400">"{p.note}"</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DebtsPage;
